/**
 * Computes a paragraph-level diff between two Slate transcript arrays.
 *
 * Both inputs are expected to be arrays of Slate `snippet` nodes coming from
 * the editor state or from the server (via `initializeTranscripts`).
 *
 * The returned diff is an ordered list of operations that the backend
 * understands and can apply on top of the previous transcript:
 *
 *  - { op: "add",    index, data }    – insert a new paragraph at `index`
 *  - { op: "update", index, data }    – replace the paragraph with `data.id`
 *  - { op: "delete", index, id }      – delete the paragraph with `id`
 *  - { op: "upsert", index, data }    – bootstrap: initial full upsert
 */
export function getTranscriptDiff(oldTranscript, newTranscript) {
  const serializedOld = serialize(oldTranscript || []);
  const serializedNew = serialize(newTranscript || []);
  const diff = [];

  let oldIndex = 0;
  let newIndex = 0;

  while (oldIndex < serializedOld.length || newIndex < serializedNew.length) {
    if (oldIndex >= serializedOld.length) {
      // Old transcript not available for comparison, all items are updates
      if (serializedOld.length === 0 && serializedNew.length > 0) {
        diff.push({ op: "upsert", index: newIndex, data: serializedNew[newIndex] });

        newIndex++;

        continue;
      }

      // All remaining items in serializedNew are additions
      if (serializedNew[newIndex].w.length > 0) {
        diff.push({ op: "add", index: newIndex, data: serializedNew[newIndex] });
      }

      newIndex++;
    } else if (newIndex >= serializedNew.length) {
      // All remaining items in serializedOld are deletions
      diff.push({ op: "delete", index: oldIndex, id: serializedOld[oldIndex].id });

      oldIndex++;
    } else if (serializedOld[oldIndex].id !== serializedNew[newIndex].id) {
      // IDs don't match, so either an addition or deletion
      const oldIdx = oldIndex;
      const newIdx = newIndex;

      const newItemIndex = serializedOld.findIndex((item, i) => i > oldIdx && item.id === serializedNew[newIdx].id);

      if (newItemIndex === -1) {
        // New item added
        if (serializedNew[newIndex].w.length > 0) {
          diff.push({ op: "add", index: newIndex, data: serializedNew[newIndex] });
        }

        newIndex++;
      } else {
        // Item deleted
        diff.push({ op: "delete", index: oldIndex, id: serializedOld[oldIndex].id });

        oldIndex++;
      }
    } else {
      // IDs match, check for updates including empty snippets
      const oldItem = serializedOld[oldIndex];
      const newItem = serializedNew[newIndex];

      if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
        // Content is different, so it's an update
        if (oldItem.w.length > 0 || newItem.w.length > 0) {
          // Include update if either old or new item had content
          const updatedItem = { ...newItem };

          if (newItem.w.length === 0 && oldItem.w.length > 0) {
            // If all text was removed, add a single space
            updatedItem.w = [{ t: " ", st: oldItem.st, e: oldItem.e, c: 1, d: false }];
          }

          diff.push({ op: "update", index: newIndex, data: updatedItem });
        }
      }

      oldIndex++;
      newIndex++;
    }
  }

  return diff;
}

export const serialize = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map((item, i) => {
    const paragraph = item?.children?.[0];
    const words = Array.isArray(paragraph?.children) ? paragraph.children : [];

    // Fallbacks to keep timings monotonic even when a paragraph is empty
    const previousParagraph = i > 0 ? data[i - 1]?.children?.[0] : null;
    const nextParagraph = i < data.length - 1 ? data[i + 1]?.children?.[0] : null;
    const previousWords = Array.isArray(previousParagraph?.children)
      ? previousParagraph.children
      : [];
    const nextWords = Array.isArray(nextParagraph?.children)
      ? nextParagraph.children
      : [];

    const previousEnd =
      previousWords.length > 0
        ? previousWords[previousWords.length - 1].end || 0
        : 0;
    const nextEnd =
      nextWords.length > 0
        ? nextWords[0].start || previousEnd
        : previousEnd;

    // When there are no words in this paragraph, emit a minimal shell so that
    // the backend still sees the paragraph but with an empty `w` array.
    if (!words.length) {
      return {
        id: item.id.toString().substring(0, 8),
        sp: item.speaker,
        st: previousEnd,
        e: nextEnd,
        w: [],
      };
    }

    return {
      id: item.id.toString().substring(0, 8),
      sp: item.speaker,
      st: words[0].start || previousEnd,
      e: words[words.length - 1].end || nextEnd,
      w: words
        .filter(word => word.text !== "")
        .map((word, index) => ({
          t: word.text,
          st: word.start || (index > 0 ? words[index - 1].end : previousEnd),
          e: word.end || (index < words.length - 1 ? words[index + 1].start : nextEnd),
          c: word.confidence ?? 0,
          d: word.is_removed ?? false,
        })),
    };
  });
};

/**
 * Detects if a new paragraph is a result of splitting an old paragraph
 * Returns the old paragraph ID if it's a split, null otherwise
 * A split is detected when the new paragraph's words form a contiguous sequence in an old paragraph
 */
function detectSplit(oldTranscript, newSnippet, oldMap) {
  const newWords = newSnippet.children[0].children
    .filter(w => w.text !== "")
    .map(w => w.text.toLowerCase().trim());

  if (newWords.length === 0) return null;

  // Check each old paragraph to see if the new paragraph's words form a contiguous sequence
  for (const [oldId, oldEntry] of oldMap.entries()) {
    const oldWords = oldEntry.item.children[0].children
      .filter(w => w.text !== "")
      .map(w => w.text.toLowerCase().trim());

    if (oldWords.length < newWords.length) continue;

    // Try to find the new words as a contiguous sequence in old words
    for (let startIdx = 0; startIdx <= oldWords.length - newWords.length; startIdx++) {
      let matches = true;

      for (let i = 0; i < newWords.length; i++) {
        if (oldWords[startIdx + i] !== newWords[i]) {
          matches = false;

          break;
        }
      }
      if (matches) {
        // Found contiguous sequence - this is likely a split
        return oldId;
      }
    }
  }

  return null;
}

/**
 * Computes word-level LCS to find matching words
 * Returns operations: 'equal', 'add', 'delete'
 */
function computeWordDiff(oldWords, newWords) {
  const oldTexts = oldWords.map(w => w.t || w.text || "");
  const newTexts = newWords.map(w => w.t || w.text || "");

  // Build LCS matrix
  const m = oldTexts.length;
  const n = newTexts.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldTexts[i - 1] === newTexts[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to get operations
  const operations = [];
  let i = m, j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTexts[i - 1] === newTexts[j - 1]) {
      operations.unshift({ type: 'equal', oldIndex: i - 1, newIndex: j - 1 });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      operations.unshift({ type: 'add', newIndex: j - 1 });
      j--;
    } else {
      operations.unshift({ type: 'delete', oldIndex: i - 1 });
      i--;
    }
  }

  return operations;
}

/**
 * Builds a Slate-compatible transcript array with diff annotations at word level
 * @param {Array} oldTranscripts - Baseline transcript (Slate format)
 * @param {Array} newTranscripts - Current transcript (Slate format)
 * @returns {Array} Slate-compatible transcript with diff annotations
 */
export function buildDiffView(oldTranscript, newTranscript) {
  if (!oldTranscript || oldTranscript.length === 0) {
    // If no baseline, return new transcripts with all marked as added
    return newTranscript.map(snippet => {
      const newSnippet = JSON.parse(JSON.stringify(snippet));

      newSnippet.children[0].children = newSnippet.children[0].children.map(word => ({
        ...word,
        diffOp: 'add'
      }));

      return newSnippet;
    });
  }

  // Get paragraph-level diff
  const diff = getTranscriptDiff(oldTranscript, newTranscript);

  // Create a map of old transcripts by ID for quick lookup
  const oldMap = new Map();

  oldTranscript.forEach((item, index) => {
    const id = item.id.toString().substring(0, 8);

    oldMap.set(id, { item, index });
  });

  // Track which new paragraphs are splits and which old paragraphs were split
  const splitMap = new Map(); // newIndex -> oldId
  const oldSplitMap = new Set(); // oldId set

  // First pass: detect splits
  diff.forEach(diffOp => {
    if (diffOp.op === 'add') {
      const newSnippet = newTranscript[diffOp.index];
      const splitOldId = detectSplit(oldTranscript, newSnippet, oldMap);

      if (splitOldId) {
        splitMap.set(diffOp.index, splitOldId);
        oldSplitMap.add(splitOldId);
      }
    }
  });

  // Create result array, maintaining order of new transcript
  const result = [];

  // Process each paragraph in new transcript order
  for (let i = 0; i < newTranscript.length; i++) {
    const newSnippet = newTranscript[i];

    // Find corresponding diff operation
    const diffOp = diff.find(d =>
      (d.op === 'add' || d.op === 'update') && d.index === i
    );

    if (diffOp && diffOp.op === 'add') {
      // Check if this is a split
      const isSplit = splitMap.has(i);
      const snippet = JSON.parse(JSON.stringify(newSnippet));

      if (!isSplit) {
        // New paragraph added (not a split) - mark all words as added
        snippet.children[0].children = snippet.children[0].children.map(word => ({
          ...word,
          diffOp: 'add'
        }));
      }

      result.push(snippet);
    } else if (diffOp && diffOp.op === 'update') {
      // Paragraph updated - do word-level diff
      const oldEntry = oldMap.get(diffOp.data.id);
      const snippet = JSON.parse(JSON.stringify(newSnippet));

      if (oldEntry) {
        const oldId = diffOp.data.id;
        const wasSplit = oldSplitMap.has(oldId);

        // If this paragraph was split, mark it for icon display
        if (wasSplit) {
          snippet.isSplit = true;
        }

        const oldWords = oldEntry.item.children[0].children.filter(w => w.text !== "");
        const newWords = snippet.children[0].children.filter(w => w.text !== "");

        // Compute word-level diff
        const wordOps = computeWordDiff(oldWords, newWords);

        // Build merged word array with annotations
        const mergedWords = [];

        for (const op of wordOps) {
          if (op.type === 'equal') {
            // Unchanged word - no annotation
            const word = JSON.parse(JSON.stringify(newWords[op.newIndex]));

            mergedWords.push(word);
          } else if (op.type === 'add') {
            // Added word
            const word = JSON.parse(JSON.stringify(newWords[op.newIndex]));

            word.diffOp = 'add';
            mergedWords.push(word);
          } else if (op.type === 'delete') {
            // Deleted word - create synthetic word (still show even if split)
            const oldWord = oldWords[op.oldIndex];
            const deletedWord = {
              ...oldWord,
              diffOp: 'delete'
            };

            mergedWords.push(deletedWord);
          }
        }

        snippet.children[0].children = mergedWords;
      } else {
        // Fallback: if old entry not found, mark all as added
        snippet.children[0].children = snippet.children[0].children.map(word => ({
          ...word,
          diffOp: 'add'
        }));
      }

      result.push(snippet);
    } else {
      // Unchanged paragraph - add as-is
      result.push(JSON.parse(JSON.stringify(newSnippet)));
    }
  }

  // Add deleted paragraphs at the end (they don't have a position in new transcript)
  diff.forEach(diffOp => {
    if (diffOp.op === 'delete') {
      const oldEntry = oldMap.get(diffOp.id);

      if (oldEntry) {
        const oldSnippet = oldEntry.item;
        const deletedSnippet = JSON.parse(JSON.stringify(oldSnippet));

        deletedSnippet.children[0].children = deletedSnippet.children[0].children.map(word => ({
          ...word,
          diffOp: 'delete'
        }));

        result.push(deletedSnippet);
      }
    }
  });

  return result;
}
